import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  useGetLeaseDepositStatusQuery,
  useInitiateStandbySessionMutation,
} from "src/store/services/privateApi";
import tokens from "src/shared/theme/tokens";

type StandbyMessage = {
  source?: string;
  version?: number;
  type?: string;
  sessionId?: string;
  depositId?: string;
  status?: string;
  reason?: string | null;
  code?: string;
  message?: string;
  height?: number;
};

type Props = {
  leaseId?: string;
  /** Used only when deposit-status omits `enabled` (current QA API). */
  propertyAbbreviation?: string | null;
};

const money = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

/**
 * Homepage security-deposit choice. Shown only when the property has
 * security deposit payment turned on and the ResMan deposit ledger has
 * an unpaid security-deposit amount. The Standby iframe starts after
 * the resident chooses Standby.
 */
export const StandbyDepositEmbed: React.FC<Props> = ({
  leaseId,
  propertyAbbreviation,
}) => {
  const navigate = useNavigate();
  const { data, isError, isLoading } = useGetLeaseDepositStatusQuery(
    leaseId ?? "",
    { skip: !leaseId }
  );

  const [initiate, { isLoading: isStarting }] =
    useInitiateStandbySessionMutation();
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const [appOrigin, setAppOrigin] = useState<string | null>(null);
  const [statusText, setStatusText] = useState<string | null>(null);
  const [iframeHeight, setIframeHeight] = useState(720);
  const recreatingRef = useRef(false);

  // A negative deposit-ledger balance means nothing is unpaid, so the choice stays hidden.
  const depositHeld = data?.depositHeld ?? 0;
  const unpaid = depositHeld < -0.01 ? 0 : data?.unpaid ?? 0;
  const owesDeposit = depositHeld < -0.01 ? false : Boolean(data?.owesDeposit);
  // Honor `enabled` when the API sends it. QA's current handler omits it;
  // EZL is the only property turned on there.
  const propertyOn =
    data?.enabled === true ||
    (data?.enabled == null &&
      (propertyAbbreviation || "").toLowerCase() === "ezl");
  const showChoice = Boolean(propertyOn && owesDeposit && unpaid > 0.01);

  const createSession = useCallback(async () => {
    if (!leaseId) return;
    try {
      const result = await initiate({ leaseId }).unwrap();
      if (!result.owesDeposit || !("url" in result)) {
        setStatusText("Standby is unavailable for this lease right now.");
        setEmbedUrl(null);
        return;
      }
      setEmbedUrl(result.url);
      setAppOrigin(result.appOrigin);
      setStatusText(
        result.ready
          ? "Standby deposit option is ready."
          : "Preparing Standby deposit option…"
      );
    } catch (e: any) {
      setEmbedUrl(null);
      setStatusText(e?.data?.message ?? "Standby is unavailable right now.");
    }
  }, [leaseId, initiate]);

  useEffect(() => {
    if (!appOrigin) return;

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== appOrigin) return;
      const msg = event.data as StandbyMessage;
      if (!msg || msg.source !== "standby" || msg.version !== 1) return;

      switch (msg.type) {
        case "standby:preparing":
          setStatusText("Preparing your Standby application…");
          break;
        case "standby:ready":
          setStatusText("Standby deposit application ready.");
          break;
        case "standby:completed":
          setStatusText(
            `Standby status: ${msg.status}${
              msg.reason ? ` (${msg.reason})` : ""
            }`
          );
          break;
        case "standby:resize":
          if (typeof msg.height === "number" && msg.height > 0) {
            setIframeHeight(msg.height);
          }
          break;
        case "standby:error":
          if (msg.code === "session_expired" && !recreatingRef.current) {
            recreatingRef.current = true;
            void createSession().finally(() => {
              recreatingRef.current = false;
            });
            break;
          }
          setStatusText(
            msg.message || `Standby error: ${msg.code || "unknown"}`
          );
          setEmbedUrl(null);
          break;
        default:
          break;
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [appOrigin, createSession]);

  if (!leaseId || isLoading || isError || !showChoice) return null;

  return (
    <div
      style={{
        marginBottom: 16,
        padding: 16,
        borderRadius: 8,
        border: `1px solid ${tokens.colors.primaryTint}`,
        background: tokens.colors.primarySoft,
      }}
    >
      <Typography
        sx={{ fontWeight: 600, marginBottom: 1, color: tokens.colors.ink }}
      >
        StandBy
      </Typography>
      {!embedUrl && !isStarting ? (
        <Typography
          sx={{
            marginBottom: 2,
            fontSize: 14,
            color: tokens.colors.inkSecondary,
          }}
        >
          You are due to pay the security deposit of {money.format(unpaid)}. You
          can choose to pay it in full or use an alternative like Standby
          Deposits.
        </Typography>
      ) : null}
      {statusText && !embedUrl ? (
        <Typography
          sx={{
            marginBottom: 1,
            fontSize: 14,
            color: tokens.colors.inkSecondary,
          }}
        >
          {statusText}
        </Typography>
      ) : null}
      {!embedUrl ? (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button
            variant="contained"
            color="primary"
            onClick={() =>
              navigate("/rent/payment", {
                state: { isFromHome: true, securityDepositAmount: unpaid },
              })
            }
          >
            Pay in full
          </Button>
          <Button
            variant="outlined"
            color="primary"
            disabled={isStarting}
            onClick={() => void createSession()}
          >
            {isStarting ? "Opening Standby…" : "Standby Deposits"}
          </Button>
        </Stack>
      ) : null}
      {embedUrl ? (
        <iframe
          id="standby-deposit"
          title="Standby deposit application"
          src={embedUrl}
          allow="payment; clipboard-write"
          referrerPolicy="no-referrer"
          style={{
            width: "100%",
            minHeight: iframeHeight,
            height: iframeHeight,
            border: 0,
            marginTop: 8,
            background: "#fff",
          }}
        />
      ) : null}
    </div>
  );
};
